import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import AppIcon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const FieldSaveStatus = ({ user, userProfile, refreshTrigger, onEvent }) => {
  const [fieldStatuses, setFieldStatuses] = useState({});
  const [testing, setTesting] = useState(false);

  const profileFields = [
    { key: 'full_name', label: 'Full Name', type: 'string', required: false },
    { key: 'email', label: 'Email', type: 'string', required: true },
    { key: 'date_of_birth', label: 'Date of Birth', type: 'date', required: false },
    { key: 'gender', label: 'Gender', type: 'string', required: false },
    { key: 'relationship_status', label: 'Relationship Status', type: 'string', required: false },
    { key: 'relationship_duration', label: 'Relationship Duration', type: 'string', required: false },
    { key: 'share_mood_data', label: 'Share Mood Data', type: 'boolean', required: false },
    { key: 'share_availability', label: 'Share Availability', type: 'boolean', required: false },
    { key: 'share_preferences', label: 'Share Preferences', type: 'boolean', required: false },
    { key: 'enable_ai_mediation', label: 'Enable AI Mediation', type: 'boolean', required: false },
    { key: 'allow_compatibility_analysis', label: 'Allow Compatibility Analysis', type: 'boolean', required: false },
    { key: 'receive_ai_suggestions', label: 'Receive AI Suggestions', type: 'boolean', required: false },
    { key: 'notify_mood_changes', label: 'Notify Mood Changes', type: 'boolean', required: false },
    { key: 'notify_schedule_reminders', label: 'Notify Schedule Reminders', type: 'boolean', required: false },
    { key: 'notify_ai_messages', label: 'Notify AI Messages', type: 'boolean', required: false }
  ];

  useEffect(() => {
    if (userProfile) {
      analyzeFields();
    }
  }, [userProfile, refreshTrigger]);

  const analyzeFields = () => {
    const statuses = {};

    profileFields?.forEach((field) => {
      const value = userProfile?.[field?.key];
      const status = {
        exists: value !== null && value !== undefined,
        value: value,
        type: typeof value,
        valid: validateField(field, value),
        lastModified: userProfile?.updated_at || userProfile?.created_at
      };

      statuses[field.key] = status;
    });

    setFieldStatuses(statuses);
    onEvent?.('Field analysis completed', 'info', statuses);
  };

  const validateField = (field, value) => {
    if (field?.required && (value === null || value === undefined || value === '')) {
      return { valid: false, reason: 'Required field is empty' };
    }

    if (value !== null && value !== undefined) {
      if (field?.type === 'string' && typeof value !== 'string') {
        return { valid: false, reason: `Expected string, got ${typeof value}` };
      }
      if (field?.type === 'boolean' && typeof value !== 'boolean') {
        return { valid: false, reason: `Expected boolean, got ${typeof value}` };
      }
      if (field?.type === 'date' && value && isNaN(Date.parse(value))) {
        return { valid: false, reason: 'Invalid date format' };
      }
    }

    return { valid: true };
  };

  const testFieldSave = async (fieldKey) => {
    setTesting(true);
    onEvent?.(`Testing save for field: ${fieldKey}`, 'action');

    try {
      const currentValue = userProfile?.[fieldKey];
      const testValue = currentValue; // Use same value to test persistence

      // Attempt update
      const { data, error } = await supabase?.from('user_profiles')?.update({ [fieldKey]: testValue })?.eq('id', user?.id)?.select()?.single();

      if (error) {
        onEvent?.(`Field save test failed: ${error?.message}`, 'error', error);
        setFieldStatuses(prev => ({
          ...prev,
          [fieldKey]: {
            ...prev?.[fieldKey],
            saveTest: { success: false, error: error?.message, timestamp: new Date() }
          }
        }));
      } else {
        // Verify the value persisted
        const { data: verifyData, error: verifyError } = await supabase?.from('user_profiles')?.select(fieldKey)?.eq('id', user?.id)?.single();

        const persisted = verifyData?.[fieldKey] === testValue;
        
        onEvent?.(
          persisted ? `Field ${fieldKey} save verified` : `Field ${fieldKey} did not persist`,
          persisted ? 'success' : 'error',
          { sent: testValue, received: verifyData?.[fieldKey] }
        );

        setFieldStatuses(prev => ({
          ...prev,
          [fieldKey]: {
            ...prev?.[fieldKey],
            saveTest: {
              success: persisted,
              error: persisted ? null : 'Value did not persist',
              timestamp: new Date(),
              sent: testValue,
              received: verifyData?.[fieldKey]
            }
          }
        }));
      }
    } catch (error) {
      onEvent?.(`Field save test exception: ${error?.message}`, 'error', error);
      setFieldStatuses(prev => ({
        ...prev,
        [fieldKey]: {
          ...prev?.[fieldKey],
          saveTest: { success: false, error: error?.message, timestamp: new Date() }
        }
      }));
    } finally {
      setTesting(false);
    }
  };

  const getStatusIcon = (field) => {
    const status = fieldStatuses?.[field?.key];
    if (!status) return { name: 'HelpCircle', color: 'text-gray-500' };

    if (status?.saveTest) {
      return status?.saveTest?.success
        ? { name: 'CheckCircle', color: 'text-green-500' }
        : { name: 'XCircle', color: 'text-red-500' };
    }

    if (!status?.valid?.valid) {
      return { name: 'AlertTriangle', color: 'text-yellow-500' };
    }

    return status?.exists
      ? { name: 'Check', color: 'text-blue-500' }
      : { name: 'Minus', color: 'text-gray-500' };
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-xl font-bold text-white">Field-by-Field Save Status</h2>
        <p className="text-sm text-gray-400 mt-1">
          Individual field validation and persistence testing
        </p>
      </div>
      {/* Field Status Grid */}
      <div className="grid grid-cols-1 gap-3">
        {profileFields?.map((field) => {
          const status = fieldStatuses?.[field?.key];
          const icon = getStatusIcon(field);

          return (
            <div
              key={field?.key}
              className="bg-gray-800 rounded-lg border border-gray-700 p-4 hover:border-gray-600 transition-colors"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3 flex-1">
                  <AppIcon name={icon?.name} className={`w-5 h-5 ${icon?.color} flex-shrink-0 mt-0.5`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-white">{field?.label}</span>
                      <span className="text-xs text-gray-500 font-mono">{field?.key}</span>
                      {field?.required && (
                        <span className="text-xs bg-red-900/30 text-red-400 px-2 py-0.5 rounded">
                          Required
                        </span>
                      )}
                    </div>

                    {/* Current Value */}
                    <div className="mt-2 text-sm">
                      <span className="text-gray-400">Current: </span>
                      <span className="text-gray-300 font-mono">
                        {status?.exists ? JSON.stringify(status?.value) : 'null'}
                      </span>
                      <span className="text-gray-500 ml-2">({status?.type})</span>
                    </div>

                    {/* Validation Status */}
                    {status && !status?.valid?.valid && (
                      <div className="mt-2 text-sm text-yellow-400 bg-yellow-900/20 rounded px-3 py-1">
                        ⚠️ {status?.valid?.reason}
                      </div>
                    )}

                    {/* Save Test Result */}
                    {status?.saveTest && (
                      <div className={`mt-2 text-sm rounded px-3 py-2 ${
                        status?.saveTest?.success
                          ? 'bg-green-900/20 text-green-400' :'bg-red-900/20 text-red-400'
                      }`}>
                        <div className="font-medium">
                          {status?.saveTest?.success ? '✓ Save Test Passed' : '✗ Save Test Failed'}
                        </div>
                        {status?.saveTest?.error && (
                          <div className="text-xs mt-1 font-mono">{status?.saveTest?.error}</div>
                        )}
                        {!status?.saveTest?.success && status?.saveTest?.sent !== undefined && (
                          <div className="text-xs mt-2 space-y-1">
                            <div>Sent: {JSON.stringify(status?.saveTest?.sent)}</div>
                            <div>Received: {JSON.stringify(status?.saveTest?.received)}</div>
                          </div>
                        )}
                        <div className="text-xs mt-1 text-gray-400">
                          {status?.saveTest?.timestamp?.toLocaleTimeString()}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Test Button */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => testFieldSave(field?.key)}
                  disabled={testing}
                  className="border-gray-600 text-gray-300 hover:bg-gray-700 flex-shrink-0"
                >
                  <AppIcon name="Play" className="w-3 h-3 mr-1" />
                  Test Save
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default FieldSaveStatus;